import { useContext, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@brainfinance/icash-component-library"

import { Onfido } from "../../Utilities/Onfido"
import { RequestsContext } from "../../../Helpers/Reducers/Requests"
import { UserContext } from "../../../Helpers/Reducers/User"
import { InstantLoanContext } from "../../../Helpers/Reducers/InstantLoan"
import { postMessageSender } from "../../../Helpers/PostMessage"
import { getOnfidoToken, getLatestIdentityVerification, getUser } from "../../../Helpers/Requests"

import faceIdIcon from "../../../images/identity-verification/iconoir_face-id.svg"
import cameraFailedIcon from "../../../images/identity-verification/camera-failed.svg"

const CAMERA_ERROR_TYPES = [
  "no_camera",
  "permissions_unavailable",
  "CameraException",
  "CameraNotDetected",
  "PermissionsUnavailable",
  "cameraPermission",
]

type OnfidoCompleteData = {
  [key: string]: unknown
}

type OnfidoError = {
  type: string
  message: string
}

export function IdentityVerificationSDK() {
  const navigate = useNavigate()

  const { state: { token } } = useContext(RequestsContext)
  const { state: { user } } = useContext(UserContext)
  const { state: { workflowType } } = useContext(InstantLoanContext)

  const [onfidoToken, setOnfidoToken] = useState<string>("")
  const [workflowRunId, setWorkflowRunId] = useState<string>("")
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isProcessingLong, setIsProcessingLong] = useState(false)

  // Guards against a second /identity-verification/initialize call (which would create a
  // second Onfido workflow run) when this component re-renders while the request is pending.
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (!token || hasInitialized.current) return

    hasInitialized.current = true

    const fetchOnfidoToken = async () => {
      try {
        setIsLoading(true)

        // The user in context is loaded once at app boot, so applicationId can be missing
        // when the application was created later in the same session. Fall back to a fresh
        // /user/info call instead of staying stuck on the loading screen forever.
        const applicationId = user?.applicationId ?? (await getUser(token))?.result?.applicationId

        if (!applicationId) {
          throw new Error("No application id available for identity verification")
        }

        const response = await getOnfidoToken(Number(applicationId), token)

        if (response.error || !response.data) {
          throw new Error(response.error?.message ?? "Failed to get Onfido token")
        }

        setOnfidoToken(response.data.sdkToken)
        setWorkflowRunId(response.data.workflowRunId)

        setIsLoading(false)
      } catch (err) {
        hasInitialized.current = false
        console.error("Error fetching Onfido token:", err)
        navigate("/identity-verification/error", {
          state: {
            title: "Identity verification",
            status: "unable_to_load",
          },
        })
      }
    }

    fetchOnfidoToken()

    postMessageSender({
      event: "marketing",
      payload: {
        status: "track",
        data: {
          event: "app_identity_verification_onfido_loaded",
        },
      },
    })
  }, [token, user?.applicationId])

  useEffect(() => {
    if (!isProcessing) {
      setIsProcessingLong(false)
      return
    }

    const timer = setTimeout(() => {
      setIsProcessingLong(true)
    }, 30000)

    return () => clearTimeout(timer)
  }, [isProcessing])

  // Polling effect when processing
  useEffect(() => {
    if (!isProcessing || !token) return

    const POLLING_INTERVAL = 5000

    const poll = async () => {
      const response = await getLatestIdentityVerification(token)

      if (response.error) {
        console.error("Polling error:", response.error)
        return
      }

      const status = response.data?.status

      if (status === "approved" || status === "manual_review") {
        clearInterval(interval)

        postMessageSender({
          event: "marketing",
          payload: {
            status: "track",
            data: {
              event: "app_identity_verification_approved",
            },
          },
        })

        navigate("/identity-verification/success", {
          state: { title: "Identity verification" },
        })
      } else if (status === "abandoned") {
        clearInterval(interval)
        navigate("/identity-verification/error", {
          state: {
            title: "Identity verification",
            status: "verification_incomplete",
          },
        })
      } else if (status === "pending_retry") {
        clearInterval(interval)
        navigate("/identity-verification/error", {
          state: {
            title: "Identity verification",
            status: "try_again",
            attemptNumber: response.data?.retryCount ?? 1,
            maxRetryAttempts: response.data?.maxRetryAttempts ?? 3,
          },
        })
      } else if (status === "expired") {
        clearInterval(interval)
        navigate("/identity-verification/error", {
          state: {
            title: "Identity verification",
            status: "session_expired",
          },
        })
      }
    }

    const interval = setInterval(poll, POLLING_INTERVAL)

    return () => clearInterval(interval)
  }, [isProcessing, token, navigate])

  const handleComplete = (data: OnfidoCompleteData) => {
    setIsProcessing(true)

    postMessageSender({
      event: "marketing",
      payload: {
        status: "track",
        data: {
          event: "app_identity_verification_onfido_completed",
          properties: {
            workflowRunId,
          },
        },
      },
    })
  }

  const handleError = (error: OnfidoError) => {
    console.error("Onfido verification error:", error)

    postMessageSender({
      event: "marketing",
      payload: {
        status: "track",
        data: {
          event: "app_identity_verification_onfido_error",
          properties: {
            errorType: error.type,
            errorMessage: error.message,
          },
        },
      },
    })

    // Check if this is a camera permission error
    if (CAMERA_ERROR_TYPES.includes(error.type)) {
      navigate("/identity-verification/error", {
        state: {
          title: "Identity verification",
          status: "camera_access",
        },
      })
    } else {
      navigate("/identity-verification/error", {
        state: {
          title: "Identity verification",
          status: "unable_to_load",
        },
      })
    }
  }

  const handleUserExit = () => {
    postMessageSender({
      event: "marketing",
      payload: {
        status: "track",
        data: {
          event: "app_identity_verification_onfido_exit",
        },
      },
    })

    navigate("/identity-verification", {
      state: { title: "Identity verification" }
    })
  }

  // Loading state while fetching token
  if (isLoading) {
    return (
      <div className="max-w-[622px] w-full flex flex-col items-center justify-center min-h-[400px]">
        <img src={faceIdIcon} alt="Identity verification" className="h-[82px] w-[82px] mb-[32px]" />
        <h2 className="title-caption !text-[#13141A] !mb-[16px] !text-center">
          Preparing verification...
        </h2>
        <p className="caption-light !text-center">
          Please wait while we set up the identity verification process.
        </p>
      </div>
    )
  }

  // Processing state after Onfido completes
  if (isProcessing) {
    return (
      <div className="max-w-[622px] w-full flex flex-col items-center justify-center min-h-[400px]">
        <img src={faceIdIcon} alt="Identity verification" className="h-[82px] w-[82px] mb-[32px]" />
        <h2 className="title-caption !text-[#13141A] !mb-[16px] !text-center">
          {isProcessingLong ? "Still processing..." : "Processing your documents..."}
        </h2>
        <p className="caption-light !text-center !mb-[32px]">
          {isProcessingLong
            ? <>This is taking a bit longer than usual. Thank you for your patience.<br/><br/>Verifying document authenticity</>
            : <>Please wait while we verify your identity. This usually takes less than a minute. Don't close or leave this screen.</>}
        </p>
      </div>
    )
  }

  return (
    <Onfido
      token={onfidoToken}
      workflowRunId={workflowRunId}
      onComplete={handleComplete}
      onError={handleError}
      onUserExit={handleUserExit}
    />
  )
}
